import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    getEmployeeRoles,
    createEmployeeRole,
    updateEmployeeRole,
    deleteEmployeeRole,
} from '../../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction';
import EmployeeTypeFormModal from './EmployeeTypeFormModal';
import DeleteRoleModal from './DeleteRoleModal';
import { usePageRefresh } from '../../../../context/PageRefreshContext';

const DEFAULT_LIMIT = 10;

const STATUS_META = {
    A: { label: 'Active', cls: 'bg-success-focus text-success-main' },
    I: { label: 'Inactive', cls: 'bg-neutral-200 text-secondary-light' },
    D: { label: 'Deleted', cls: 'bg-danger-focus text-danger-main' },
};

const menusOf = (access) => {
    if (Array.isArray(access?.menus)) return access.menus;
    if (Array.isArray(access)) return access;
    return [];
};

const EmployeeTypesListLayer = () => {
    const dispatch = useDispatch();
    const {
        rolesData, rolesLoading, rolesPagination,
        roleCreateLoading, roleUpdateLoading, roleDeleteLoading,
    } = useSelector((state) => state.CtrlEmployeeReducer || {});

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState(''); // '', 'A', 'I', 'D'
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const [formState, setFormState] = useState({ open: false, mode: 'add', role: null });
    const [deleteRole, setDeleteRole] = useState(null);

    const roles = Array.isArray(rolesData) ? rolesData : [];
    const totalRecords = rolesPagination?.total ?? roles.length;

    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchList = useCallback(() => {
        dispatch(getEmployeeRoles({
            page, limit,
            search: search || undefined,
            status: status || undefined,
        }));
    }, [dispatch, page, limit, search, status]);

    useEffect(() => { fetchList(); }, [fetchList]);
    usePageRefresh(fetchList);

    const statusFilterOptions = useMemo(() => ([
        { value: 'all', label: 'All (excl. deleted)' },
        { value: 'A', label: 'Active' },
        { value: 'I', label: 'Inactive' },
        { value: 'D', label: 'Deleted' },
    ]), []);

    const handleSubmit = (payload, { isEdit }) => {
        const action = isEdit ? updateEmployeeRole : createEmployeeRole;
        dispatch(action({
            ...payload,
            onSuccess: () => { setFormState({ open: false, mode: 'add', role: null }); fetchList(); },
        }));
    };

    const handleDelete = () => {
        if (!deleteRole) return;
        dispatch(deleteEmployeeRole({
            id: deleteRole.employee_type_id ?? deleteRole.id,
            onSuccess: () => { setDeleteRole(null); fetchList(); },
        }));
    };

    const startIndex = (page - 1) * limit;

    return (
        <>
            <div className="card">
                <div className="card-header border-bottom">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="icon-field">
                                <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search type name" className="form-control form-control-sm w-auto" style={{ minWidth: 220 }} />
                                <span className="icon"><Icon icon="ion:search-outline" /></span>
                            </div>
                            <div style={{ width: 190 }}>
                                <SearchableDropdown
                                    value={status || 'all'}
                                    onChange={(v) => { setStatus(v === 'all' ? '' : v); setPage(1); }}
                                    options={statusFilterOptions}
                                    placeholder="Status" clearable={false} small
                                />
                            </div>
                        </div>
                        <button onClick={() => setFormState({ open: true, mode: 'add', role: null })}
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1">
                            <Icon icon="mdi:plus" /> Add Type
                        </button>
                    </div>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Accessible Menus</th>
                                    <th scope="col">Switch to Admin</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rolesLoading ? (
                                    <tr><td colSpan={6} className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                        <span className="ms-2 text-secondary-light">Loading…</span>
                                    </td></tr>
                                ) : roles.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-4 text-secondary-light">No employee types found</td></tr>
                                ) : (
                                    roles.map((role, index) => {
                                        const menus = menusOf(role.access);
                                        const sm = STATUS_META[role.status] || STATUS_META.A;
                                        const isDeleted = role.status === 'D';
                                        return (
                                            <tr key={role.employee_type_id ?? index}>
                                                <td>{startIndex + index + 1}</td>
                                                <td className="text-md fw-medium">{role.type}</td>
                                                <td>
                                                    {menus.length === 0 ? (
                                                        <span className="text-xs text-secondary-light">—</span>
                                                    ) : (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {menus.slice(0, 4).map((m) => (
                                                                <span key={m} className="badge bg-primary-50 text-primary-600 text-xs">{m}</span>
                                                            ))}
                                                            {menus.length > 4 && (
                                                                <span className="badge bg-neutral-200 text-secondary-light text-xs">+{menus.length - 4}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {role.switch_to_admin === 1 || role.switch_to_admin === true ? (
                                                        <span className="badge bg-success-focus text-success-main text-xs">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-neutral-200 text-secondary-light text-xs">No</span>
                                                    )}
                                                </td>
                                                <td><span className={`badge ${sm.cls} text-xs`}>{sm.label}</span></td>
                                                <td>
                                                    <Tooltip title={isDeleted ? 'Restore via edit' : 'Edit'} placement="top" arrow>
                                                        <button onClick={() => setFormState({ open: true, mode: 'edit', role })}
                                                            className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0">
                                                            <Icon icon="lucide:edit" />
                                                        </button>
                                                    </Tooltip>
                                                    {!isDeleted && (
                                                        <Tooltip title="Delete" placement="top" arrow>
                                                            <button onClick={() => setDeleteRole(role)}
                                                                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0">
                                                                <Icon icon="mingcute:delete-2-line" />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="card-footer">
                    <PaginationLimit
                        currentPage={page}
                        totalRecords={totalRecords}
                        limit={limit}
                        handleLimitChange={(e) => { setLimit(Number(e.target.value) || DEFAULT_LIMIT); setPage(1); }}
                        handlePageChange={(p) => { if (p >= 1) setPage(p); }}
                    />
                </div>
            </div>

            <EmployeeTypeFormModal
                open={formState.open}
                mode={formState.mode}
                role={formState.role}
                submitting={roleCreateLoading || roleUpdateLoading}
                onClose={() => setFormState({ open: false, mode: 'add', role: null })}
                onSubmit={handleSubmit}
            />

            <DeleteRoleModal
                open={!!deleteRole}
                role={deleteRole}
                isLoading={roleDeleteLoading}
                handleClose={() => setDeleteRole(null)}
                handleConfirm={handleDelete}
            />
        </>
    );
};

export default EmployeeTypesListLayer;
